import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { catalogApi } from '@/api';
import { Alert, Button, Checkbox, Input, Select } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { studentRegisterSchema } from '@/validations';

import { AuthCard } from './AuthCard';

export function RegisterStudentPage() {
  const { registerStudent } = useAuth();
  const navigate = useNavigate();
  // From an invite link. The server ignores a code that matches nobody.
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref')?.trim() || null;

  const { data: universities } = useQuery({
    queryKey: ['universities'],
    queryFn: () => catalogApi.listUniversities(),
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(studentRegisterSchema),
    defaultValues: { accepted_terms: false },
  });

  async function onSubmit(values) {
    try {
      const session = await registerStudent({
        ...values,
        university_id: values.university_id || null,
        university_email: values.university_email || null,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      navigate(`${paths.verifyEmail}?email=${encodeURIComponent(session.user.email)}`, {
        state: { debugCode: session.verification_code_debug ?? null, codeSent: true },
      });
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        setError('root', { message: errorMessage(error, 'Could not create the account') });
      }
    }
  }

  return (
    <AuthCard
      title="Create your student account"
      description="One profile for every internship application."
      accountSwitch
      heading="Your first internship starts with one profile."
      points={[
        'Apply to any listing with the same profile and CV',
        'Recommendations that learn what you are good at',
        'Track every application from applied to hired',
      ]}
      footer={
        <>
          Hiring instead?{' '}
          <Link to={paths.registerCompany} className="text-brand-600 font-medium">
            Create a company account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errors.root && <Alert tone="error">{errors.root.message}</Alert>}
        {referralCode && (
          <p className="text-brand-700 bg-brand-50 rounded-lg px-3 py-2 text-xs font-medium">
            Invited by a friend. Welcome aboard.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Full name"
            autoComplete="name"
            required
            error={errors.full_name?.message}
            {...register('full_name')}
          />
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
        </div>
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 8 characters, with upper and lower case and a number."
          error={errors.password?.message}
          {...register('password')}
        />

        <p className="text-ink-muted flex items-center gap-2 pt-1 text-[11px] font-semibold tracking-wide uppercase">
          Your studies — optional
          <span aria-hidden="true" className="bg-line h-px flex-1" />
        </p>
        <p className="text-ink-muted -mt-2 text-xs">
          You can skip these and add them later from your profile.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="University"
            optional
            error={errors.university_id?.message}
            {...register('university_id')}
          >
            <option value="">Select later</option>
            {universities?.map((university) => (
              <option key={university.id} value={university.id}>
                {university.name}
              </option>
            ))}
          </Select>

          <Input
            label="University email"
            optional
            type="email"
            hint="Verifying it adds a trust badge companies can see."
            error={errors.university_email?.message}
            {...register('university_email')}
          />
        </div>

        <Checkbox
          label={
            <>
              I accept the <span className="underline">terms</span> and{' '}
              <span className="underline">privacy policy</span>
            </>
          }
          error={errors.accepted_terms?.message}
          {...register('accepted_terms')}
        />

        <Button type="submit" fullWidth isLoading={isSubmitting}>
          Create account
        </Button>
      </form>
    </AuthCard>
  );
}
